import { counted } from '../format';
import { cleanPhone } from '../phone';

/**
 * Members, their contact, the committee, the shart people and their
 * documents: the rules shared by the committee phone app and the desk
 * (docs/changes/41). Each app keeps its own GraphQL documents.
 */

export type PhoneStatus = 'OK' | 'MISSING' | 'INVALID' | 'LANDLINE';
export type NotifyChannel = 'WHATSAPP' | 'SMS' | 'NONE';
export type MemberSex = 'MALE' | 'FEMALE';
export type MemberStatus = 'ACTIVE' | 'LEFT';

/** Messages (invoices, reminders) can reach the member by WhatsApp/SMS. */
export const isReachable = (m: {
  phoneStatus: PhoneStatus | null;
  notifyChannel: NotifyChannel;
}) => m.phoneStatus === 'OK' && m.notifyChannel !== 'NONE';

export const PHONE_STATUS_LABELS: Record<PhoneStatus, string> = {
  OK: 'هاتف صالح',
  MISSING: 'بدون هاتف',
  INVALID: 'رقم غير صحيح',
  LANDLINE: 'هاتف ثابت',
};

export const CHANNEL_LABELS: Record<NotifyChannel, string> = {
  WHATSAPP: 'واتساب',
  SMS: 'رسالة قصيرة (SMS)',
  NONE: 'لا يريد رسائل',
};

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  ACTIVE: 'منخرط',
  LEFT: 'غادر الجمعية',
};

/** arqam doc 44, in the committee's order. */
export const COMMITTEE_ROLES = [
  'PRESIDENT',
  'VICE_PRESIDENT',
  'SECRETARY',
  'VICE_SECRETARY',
  'TREASURER',
  'VICE_TREASURER',
  'ADVISOR',
] as const;
export type CommitteeRole = (typeof COMMITTEE_ROLES)[number];

export const COMMITTEE_ROLE_LABELS: Record<CommitteeRole, string> = {
  PRESIDENT: 'الرئيس',
  VICE_PRESIDENT: 'نائب الرئيس',
  SECRETARY: 'الكاتب العام',
  VICE_SECRETARY: 'نائب الكاتب العام',
  TREASURER: 'أمين المال',
  VICE_TREASURER: 'نائب أمين المال',
  ADVISOR: 'مستشار',
};

export function byCommitteeRole<
  T extends { committeeRole: CommitteeRole | null },
>(members: T[]): T[] {
  const rank = (role: CommitteeRole | null) =>
    role ? COMMITTEE_ROLES.indexOf(role) : COMMITTEE_ROLES.length;
  return [...members].sort(
    (a, b) => rank(a.committeeRole) - rank(b.committeeRole),
  );
}

export type MemberFormValues = {
  firstName: string;
  fatherName: string;
  lastName: string;
  sex: MemberSex;
  memberAddress: string;
  phone: string;
  contactPhone: string;
  contactName: string;
  notifyChannel: NotifyChannel;
  email: string;
  /** Role on the committee; null = not on the committee (arqam doc 44). */
  committeeRole: CommitteeRole | null;
};

/** A phone already validated by the form, cleaned for the API. */
const validPhone = (input: string | undefined) => {
  const result = cleanPhone(input ?? '');
  return 'phone' in result ? result.phone : null;
};

/** Form values → API fields: phones cleaned, empty optional fields → null. */
export function toMemberFields(values: MemberFormValues) {
  const contactPhone = validPhone(values.contactPhone);
  return {
    firstName: values.firstName.trim(),
    fatherName: (values.fatherName ?? '').trim(),
    lastName: values.lastName.trim(),
    sex: values.sex,
    // Empty: arqam puts the default address from settings (its doc 40).
    memberAddress: (values.memberAddress ?? '').trim(),
    phone: validPhone(values.phone),
    contactPhone,
    contactName: contactPhone ? values.contactName?.trim() || null : null,
    notifyChannel: values.notifyChannel,
    email: values.email?.trim() || null,
    committeeRole: values.committeeRole ?? null,
  };
}

/** arqam's member errors (its docs 13, 33, 41), in Arabic. */
export const MEMBER_ERRORS = {
  INVALID_PHONE: ({ field }: Record<string, unknown>) =>
    field === 'contactPhone' ? 'هاتف القريب غير صحيح.' : 'هاتف العضو غير صحيح.',
  REASON_REQUIRED: 'اكتب السبب.',
  STATUS_UNCHANGED: 'العضو في هذه الحالة أصلاً.',
  DATE_INVALID: 'تاريخ غير صحيح (لا يكون في المستقبل).',
  MEMBER_HAS_ACTIVE_METERS:
    'للعضو عداد مربوط أو موقوف أو معطّل: أغلقه أو استرجعه أولاً.',
};

/** arqam's placeholder for people billed without a name (its doc 45). */
export const UNNAMED_SHART_PERSON = 'شخص غير مسمى';

export const isUnnamed = (name: string) => name.trim() === UNNAMED_SHART_PERSON;

/** Arabic messages for the shart people mutations. */
export const SHART_ERRORS = {
  'CHECK_VIOLATION:shart_people_name_required': 'الاسم مطلوب',
  FORBIDDEN: 'تعديل الشرط من اختصاص اللجنة.',
};

export const peopleCount = (n: number) =>
  counted(n, {
    one: 'شخص واحد',
    two: 'شخصان',
    few: 'أشخاص',
    many: 'شخصاً',
    hundred: 'شخص',
  });

/** A member's documents, as messages with their signed link. */
export type MemberDocument = 'statement' | 'notice';

export const DOCUMENT_TITLES: Record<MemberDocument, string> = {
  statement: 'كشف الحساب',
  notice: 'إشعار الأداء',
};

export function documentMessage(
  document: MemberDocument,
  memberName: string | null,
  associationName: string,
  link: string,
): string {
  const what =
    document === 'statement'
      ? `هذا كشف حسابكم لدى ${associationName} (الفواتير والأداءات لآخر 12 شهراً):`
      : `هذا إشعار بالفواتير غير المؤداة لدى ${associationName}:`;
  return [
    memberName ? `السلام عليكم ${memberName}،` : 'السلام عليكم،',
    what,
    link,
    'شكراً لكم.',
  ].join('\n');
}
